/**
 * @fileoverview Custom hook and component for watching Formik form values changes.
 */

import {useFormikContext} from "formik";
import {useDeepCompareEffect} from "react-use";

/**
 * Props for the useFormikWatchValuesChange hook.
 *
 * @template T The type of the form values.
 * @param {Function} onChangeValues Callback function called when form values change.
 */
type UseFormikWatchValuesChangeProps<T> = {
  onChangeValues: (values: T) => void;
};

/**
 * Custom hook that watches Formik form values and calls a callback when they change.
 * Uses deep comparison to detect changes in complex objects.
 *
 * @template T The type of the form values.
 * @param {UseFormikWatchValuesChangeProps<T>} props The hook props.
 * @param {Function} props.onChangeValues Callback function called when form values change.
 * @returns {null} Always returns null.
 */
export const useFormikWatchValuesChange = <T,>({
  onChangeValues,
}: UseFormikWatchValuesChangeProps<T>): null => {
  const formik = useFormikContext<T>();

  useDeepCompareEffect(() => {
    onChangeValues(formik.values);
  }, [formik.values]);

  return null;
};

/**
 * Component that uses the useFormikWatchValuesChange hook.
 * Renders nothing but triggers the value change watching logic.
 *
 * @template T The type of the form values.
 * @param {UseFormikWatchValuesChangeProps<T>} props The component props.
 * @returns {JSX.Element} A null element that performs the value watching.
 */
export const FormikWatchValuesChange = <T,>(
  props: UseFormikWatchValuesChangeProps<T>,
) => <>{useFormikWatchValuesChange(props)}</>;
